import { useQuery } from '@tanstack/react-query';

import UsersService from '~/services/UsersService';

import { getUserTenant, getUserTenantTitle } from '~/utils/userIdentity';

export interface UserBreadcrumbData {
  user: {
    label: string;
    href: string;
    tenant: string;
  };
}

interface UseUserBreadcrumbsParams {
  userId: string | null;
  enabled?: boolean;
}

interface UseUserBreadcrumbsResult {
  data: UserBreadcrumbData | null;
  isLoading: boolean;
  error: Error | null;
}

async function fetchUserBreadcrumbData(userId: string): Promise<UserBreadcrumbData> {
  const user = await UsersService.getUser(userId);

  const userName = user?.fullName || user?.givenNames || user?.email || 'User';
  const tenantTitle = getUserTenantTitle(user ?? {});

  return {
    user: {
      label: tenantTitle ? `${userName} — ${tenantTitle}` : userName,
      href: `/users/${userId}`,
      tenant: getUserTenant(user ?? {}) ?? '',
    },
  };
}

export default function useUserBreadcrumbs({
  userId,
  enabled = true,
}: UseUserBreadcrumbsParams): UseUserBreadcrumbsResult {
  const isEnabled = enabled && Boolean(userId);

  const { data, isLoading, error } = useQuery<UserBreadcrumbData>({
    queryKey: ['user-breadcrumbs', userId],
    queryFn: async () => {
      if (!userId) {
        throw new Error('User ID is required');
      }
      return fetchUserBreadcrumbData(userId);
    },
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - user data doesn't change often
    retry: false,
  });

  return {
    data: data ?? null,
    isLoading,
    error: error as Error | null,
  };
}
